/** Asks a project or workspace's admins for access from a refusal screen.
 *
 * Mirrors SupportTicketService: it reads the persisted token directly because
 * the refusal screen can render outside the auth providers, and a rejected
 * request must never refresh or end the session. The sent state is kept per
 * user and resource for the browser session so a refresh cannot invite a
 * second request; the server de-duplicates for 24 hours regardless.
 */

import { API_BASE } from '../utils/supabase/client';
import { peekStoredSession } from '../platform/auth/sessionBootstrapPolicy';
import type { AccessRequestTarget } from '../utils/accessRequestTarget';

export const ACCESS_REQUEST_ENDPOINT = `${API_BASE}/access-requests`;
export const ACCESS_REQUEST_MAX_NOTE = 500;

export type AccessRequestOutcome =
  | { ok: true; fallback?: 'support' }
  | { ok: false; error: string };

function userIdFromToken(token: string): string {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const sub = JSON.parse(atob(payload)).sub;
    return typeof sub === 'string' ? sub : 'unknown';
  } catch {
    return 'unknown';
  }
}

export function accessRequestKey(target: AccessRequestTarget): string {
  const peek = peekStoredSession();
  const user = peek ? userIdFromToken(peek.accessToken) : 'anon';
  const resource = target.projectId ?? `${target.clientSlug ?? ''}/${target.projectSlug ?? ''}`;
  const workspace = target.workspaceId ?? target.workspaceSlug ?? '';
  return `quantis:access-request:${user}:${workspace}:${resource}:${target.section ?? ''}`;
}

export function readAccessRequestSent(target: AccessRequestTarget): 'support' | 'sent' | null {
  try {
    const value = sessionStorage.getItem(accessRequestKey(target));
    return value === 'support' || value === 'sent' ? value : null;
  } catch {
    return null;
  }
}

export function rememberAccessRequestSent(target: AccessRequestTarget, fallback?: 'support'): void {
  try { sessionStorage.setItem(accessRequestKey(target), fallback ?? 'sent'); } catch { /* private mode: the server still de-duplicates */ }
}

export async function postAccessRequest(target: AccessRequestTarget, note: string): Promise<AccessRequestOutcome> {
  const peek = peekStoredSession();
  if (!peek || (peek.expiresAt !== null && peek.expiresAt * 1000 <= Date.now())) {
    return { ok: false, error: 'Sign in again to send this request.' };
  }
  const trimmed = note.trim();
  try {
    const res = await fetch(ACCESS_REQUEST_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${peek.accessToken}` },
      body: JSON.stringify({ ...target, ...(trimmed ? { note: trimmed } : {}) }),
    });
    if (res.status === 429) return { ok: false, error: 'Too many requests. Try again later.' };
    if (!res.ok) return { ok: false, error: "We couldn't send that. Try again." };
    const body = await res.json().catch(() => ({}));
    return { ok: true, fallback: body?.fallback === 'support' ? 'support' : undefined };
  } catch {
    return { ok: false, error: "We couldn't send that. Check your connection and try again." };
  }
}
